import {
  api,
  clearImpersonationUserId,
  getImpersonationUserId,
  setImpersonationUserId,
} from "./api";

/**
 * "View as user" orchestration for iQuest staff.
 *
 * The active target lives in sessionStorage (see api.ts); api.req() reads it on
 * every request and attaches the X-Impersonate-User header, so the backend runs
 * each request as that client user (strictly read-only).
 *
 * Both transitions HARD-RELOAD to "/" rather than mutating SWR caches: flipping
 * the effective identity touches every cached read, and a reload guarantees a
 * clean slate (MSAL re-inits, all data refetches under the new identity) with
 * no risk of stale per-page state. The header timing matters — see each fn.
 */

export { getImpersonationUserId };

/** Begin a read-only view-as session for `userId`, then reload as that user. */
export async function startImpersonation(userId: string): Promise<void> {
  // start runs FIRST, before the header is set, so it executes as the real
  // staff member and is audit-logged as such (and not blocked as a write).
  await api.impersonation.start(userId);
  setImpersonationUserId(userId);
  window.location.assign("/");
}

/** End the active view-as session and reload back into the staff view. */
export async function stopImpersonation(): Promise<void> {
  const id = getImpersonationUserId();
  // Clear the header FIRST so the stop call runs as the real staff member and
  // isn't caught by the read-only guard. Reload regardless of the audit call.
  clearImpersonationUserId();
  try {
    if (id) await api.impersonation.stop(id);
  } finally {
    window.location.assign("/");
  }
}
